import { useRestApiProvider } from "@jugl-web/rest-api";
import { Buffer } from "buffer";
import mime from "mime";
import { useCallback, useRef } from "react";

// TODO: add error handling
export const useDownloadAttachment = () => {
  const { driveApi } = useRestApiProvider();
  const [downloadChunk] = driveApi.useDownloadAttachmentChunkMutation();

  const downloadInProgress = useRef<boolean>(false);
  const downloadCancelled = useRef<boolean>(false);

  const cancelDownload = useCallback(() => {
    if (!downloadInProgress.current) {
      return;
    }
    downloadCancelled.current = true;
  }, []);

  const download = useCallback(
    async ({
      path,
      fileSize,
      mimeType,
      fileName,
      onProgress,
    }: {
      path: string;
      fileName: string;
      fileSize: number;
      mimeType: string;
      onProgress?: (
        bytesSent: number,
        totalBytes: number,
        cancelDownload: () => void
      ) => void;
    }) => {
      downloadInProgress.current = true;

      const chunkByteSize = 1 * 1024 * 1024;

      const numChunks = Math.ceil(fileSize / chunkByteSize);
      let file = new File([], fileName, { type: mimeType });

      const chunks: { start: number; end: number; chunk: string }[] = [];
      for (let i = 0; i < numChunks; i += 1) {
        if (downloadCancelled.current) {
          downloadCancelled.current = false;
          downloadInProgress.current = false;
          // TODO: better error
          throw new Error("Cancelled");
        }

        const start = i * chunkByteSize;
        const end = Math.min(fileSize, start + chunkByteSize) - 1;
        // oxlint-disable-next-line no-await-in-loop
        const response = await downloadChunk({
          params: {
            path,
            range: [start, end],
          },
        });
        onProgress?.(end, fileSize, cancelDownload);
        if ("data" in response) {
          chunks.push({ start, end, chunk: response.data });
        }
      }
      downloadInProgress.current = false;
      if (downloadCancelled.current) {
        downloadCancelled.current = false;
        // TODO: better error
        throw new Error("Cancelled");
      }

      chunks.forEach(({ chunk, start }) => {
        const chunkData = new Uint8Array(Buffer.from(chunk, "base64"));
        const chunkBlob = new Blob([chunkData], {
          type: mimeType,
        });
        const fileBlob = new Blob([file.slice(0, start), chunkBlob], {
          type: mimeType,
        });
        file = new File(
          [fileBlob],
          `${fileName}.${mime.getExtension(mimeType)}`,
          {
            type: mimeType,
          }
        );
      });
      return file;
    },
    [downloadCancelled, downloadChunk, cancelDownload]
  );

  return { download, cancelDownload, downloadInProgress };
};
